import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import { exploreUnknownRunFailureLabel } from './utils/exploreUnknownRunFailureLabel';
import { formatExploreUnknownMissionsWithEasyCount } from './utils/formatExploreUnknownMissionsWithEasyCount';
import './WelcomeExploreUnknownLeaderboard.css';

const POLL_MS = 30000;

function failureReasonLabel(run, t, getTranslatedFactionName) {
  return exploreUnknownRunFailureLabel(run, t, getTranslatedFactionName, { secondPerson: false });
}

/**
 * @param {{ isMobile?: boolean }} props
 */
function WelcomeExploreUnknownLeaderboard({ isMobile = false }) {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState(null);
  const [runs, setRuns] = useState([]);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [factionTranslations, setFactionTranslations] = useState({});

  const getTranslatedFactionName = useCallback(
    (factionName) => {
      if (!factionName) {
        return factionName;
      }
      return factionTranslations[factionName] || factionName;
    },
    [factionTranslations]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchFactionTranslations = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (!cancelled) {
            setFactionTranslations({});
          }
          return;
        }
        const currentLanguage = i18n.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        const response = await fetch(
          `${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`factions HTTP ${response.status}`);
        }
        const factions = await response.json();
        if (cancelled) {
          return;
        }
        const translationMap = {};
        factions.forEach((faction) => {
          translationMap[faction.english_name] = faction.name;
        });
        setFactionTranslations(translationMap);
      } catch (err) {
        console.error('Welcome: faction translation fetch failed:', err);
        if (!cancelled) {
          setFactionTranslations({});
        }
      }
    };

    fetchFactionTranslations();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  useEffect(() => {
    let cancelled = false;

    const loadLeaderboard = async () => {
      try {
        const response = await fetch(`${API_URL}/explore-unknown/welcome-leaderboard`);
        if (!response.ok) {
          const errText = await response.text().catch(() => response.statusText);
          throw new Error(`welcome-leaderboard HTTP ${response.status}: ${errText}`);
        }
        const data = await response.json();
        if (typeof data.period !== 'string') {
          throw new Error('welcome-leaderboard response missing period');
        }
        if (!Array.isArray(data.runs)) {
          throw new Error('welcome-leaderboard response missing runs array');
        }
        if (!cancelled) {
          setPeriod(data.period);
          setRuns(data.runs);
          setFetchFailed(false);
          setLoading(false);
        }
      } catch (err) {
        console.error('Welcome: explore-unknown welcome-leaderboard fetch failed:', err);
        if (!cancelled) {
          setFetchFailed(true);
          setLoading(false);
        }
      }
    };

    loadLeaderboard();
    const id = setInterval(loadLeaderboard, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const titleKey =
    period === 'week'
      ? 'welcome.exploreUnknownLeaderboard.titleWeek'
      : 'welcome.exploreUnknownLeaderboard.titleDay';

  return (
    <aside
      className={`welcome-eu-leaderboard${isMobile ? ' welcome-eu-leaderboard--mobile' : ''}`}
      aria-label={t('welcome.exploreUnknownLeaderboard.ariaLabel')}
    >
      <span className="welcome-eu-leaderboard-corner welcome-eu-leaderboard-corner--tr" aria-hidden="true" />
      <span className="welcome-eu-leaderboard-corner welcome-eu-leaderboard-corner--br" aria-hidden="true" />
      <div className="welcome-eu-leaderboard-header">
        <img
          src="/images/eclipsen.png"
          alt=""
          className="welcome-eu-leaderboard-icon"
          aria-hidden="true"
        />
        <h2 className="welcome-eu-leaderboard-title">{t(titleKey)}</h2>
      </div>

      {loading && runs.length === 0 && !fetchFailed && (
        <p className="welcome-eu-leaderboard-status">{t('welcome.exploreUnknownLeaderboard.loading')}</p>
      )}

      {fetchFailed && runs.length === 0 && (
        <p className="welcome-eu-leaderboard-status welcome-eu-leaderboard-status--error">
          {t('welcome.exploreUnknownLeaderboard.loadFailed')}
        </p>
      )}

      {!fetchFailed && !loading && runs.length === 0 && (
        <p className="welcome-eu-leaderboard-status">{t('welcome.exploreUnknownLeaderboard.empty')}</p>
      )}

      {runs.length > 0 && (
        <ol className="welcome-eu-leaderboard-list welcome-eu-leaderboard-list--scroll">
          {runs.map((run, index) => (
            <li key={`${run.username}-${run.missionReached}-${index}`} className="welcome-eu-leaderboard-entry">
              <span className="welcome-eu-leaderboard-rank">{index + 1}</span>
              <div className="welcome-eu-leaderboard-body">
                <div className="welcome-eu-leaderboard-player">{run.username}</div>
                <div className="welcome-eu-leaderboard-mission">
                  {formatExploreUnknownMissionsWithEasyCount(
                    run.missionReached,
                    run.easyMissionsCompleted
                  )}
                </div>
                <div
                  className={`welcome-eu-leaderboard-failure${
                    run.bossIntroTranslationKey ? ' welcome-eu-leaderboard-failure--boss' : ''
                  }`}
                >
                  {failureReasonLabel(run, t, getTranslatedFactionName)}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

export default WelcomeExploreUnknownLeaderboard;
